import React from 'react';
export function HeroSection() {
  const handleCtaClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // 1. Force every LazyMount on the page to render NOW so the pricing
    //    section's final position is computed against real content,
    //    not placeholder min-heights. Without this, intermediate sections
    //    grow as we scroll past them and the target drifts down.
    window.dispatchEvent(new Event('mp:eager-mount'));
    // 2. Wait two animation frames so React commits + browser lays out,
    //    then perform a single smooth scroll to the real pricing position.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const pricing = document.getElementById('pricing');
        if (!pricing) return;
        // Use absolute Y to avoid any rounding issues and to land
        // pricing flush at the top of the viewport on mobile.
        const top = pricing.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      });
    });
  };
  return <section id="hero-section" className="relative w-full flex flex-col text-center px-4 pt-4 pb-6 sm:pt-12 sm:pb-20 items-center justify-center bg-bg-main overflow-hidden" data-id="element-229">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.15) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-230"></div>

      {/* Attention Badge */}
      <div className="relative bg-badge-red/10 border border-badge-red/20 text-badge-red font-bold text-[10px] sm:text-xs px-3 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 sm:mb-6 flex items-center gap-1.5 sm:gap-2" data-id="element-231">
        <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-badge-red animate-pulse-composited" data-id="element-232"></span>
        <span className="hidden sm:inline" data-id="element-233">
          ATTENZIONE: INSEGNANTI DI SCUOLA PRIMARIA STANCHE DI IMPROVVISARE
          L'ORA DI MUSICA SUI SITI O PINTEREST
        </span>
        <span className="sm:hidden" data-id="element-234">
          ATTENZIONE: INSEGNANTI DI SCUOLA PRIMARIA
        </span>
      </div>

      {/* Main headline */}
      <h1 className="relative font-black text-text-main max-w-5xl sm:leading-[1.05] mb-2 sm:mb-6 tracking-tight" data-id="element-235">
        <span className="block text-[26px] sm:text-5xl md:text-6xl" data-id="element-236">
          Pianifica un anno intero di{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400" data-id="element-237">
            MUSICA
          </span>{' '}
          in 5 minuti.
        </span>
      </h1>

      {/* Subheadline */}
      <div className="relative w-full max-w-3xl mx-auto mb-3 sm:mb-8 sm:bg-bg-alt sm:border sm:border-white/10 sm:border-l-4 sm:border-l-primary sm:px-8 sm:py-5 sm:rounded-r-xl sm:text-left" data-id="element-238">
        <p className="text-text-main text-[14px] sm:text-xl font-semibold leading-snug" data-id="element-239">
          Il primo Kit{' '}
          <span className="text-orange-400 font-bold" data-id="element-240">"Apri e Insegna"</span>{' '}
          che ti restituisce i{' '}
          <span className="text-cta-green font-bold" data-id="element-241">weekend liberi</span> —
          dalla 1ª alla 5ª classe.
        </p>
        <div className="hidden sm:flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm font-medium text-text-sec" data-id="element-242">
          <span className="flex items-center gap-1.5" data-id="element-243">
            <span className="text-cta-green font-bold" data-id="element-244">✓</span>
            <span data-id="element-245">Programmazione PTOF</span>
          </span>
          <span className="flex items-center gap-1.5" data-id="element-246">
            <span className="text-cta-green font-bold" data-id="element-247">✓</span>
            <span data-id="element-248">6 Volumi + Unità</span>
          </span>
          <span className="flex items-center gap-1.5" data-id="element-249">
            <span className="text-cta-green font-bold" data-id="element-250">✓</span>
            <span data-id="element-251">Basi audio incluse</span>
          </span>
          <span className="flex items-center gap-1.5" data-id="element-252">
            <span className="text-cta-green font-bold" data-id="element-253">✓</span>
            <span data-id="element-254">+200 ore risparmiate</span>
          </span>
        </div>
      </div>

      {/* Mockup image */}
      <div className="relative w-full max-w-[260px] sm:max-w-[480px] md:max-w-[560px] mx-auto mb-2 sm:mb-8" data-id="element-255">
        <div className="absolute inset-0 rounded-full pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(234,88,12,0.15) 0%, transparent 70%)'
      }} aria-hidden="true" data-id="element-256"></div>
        <img src="https://cdn.magicpatterns.com/uploads/6sQdRKwq64Td5ZAfjEvk8H/Diseno-sin-titulo-_41_.webp" alt="Musica Pronta — Kit Didattico Completo (Volume 1-6)" className="relative w-full h-auto object-contain" width={420} height={420} sizes="(max-width: 640px) 220px, (max-width: 768px) 360px, 420px" fetchPriority="high" data-id="element-257" />
      </div>

      {/* Stars + Urgency */}
      <div className="relative flex flex-col items-center gap-1.5 sm:gap-2 mb-3 sm:mb-6" data-id="element-258">
        <div className="flex items-center gap-2" data-id="element-259">
          <span className="text-secondary text-sm sm:text-lg" data-id="element-260">⭐⭐⭐⭐⭐</span>
          <span className="text-text-sec text-xs sm:text-base font-medium" data-id="element-261">
            4.9/5 (+1.200 Recensioni Verificate)
          </span>
        </div>
        <span className="text-secondary font-bold text-xs sm:text-sm bg-secondary/10 px-3 py-1 rounded-full border border-secondary/20" data-id="element-262">
          🔥 84 insegnanti hanno scelto questo Kit oggi
        </span>
      </div>

      {/* CTA Button */}
      <a href="#pricing" onClick={handleCtaClick} className="relative btn-primary-pulse bg-cta-green font-black px-6 sm:px-12 py-3 sm:py-4 rounded-2xl hover:bg-cta-green-hover transition-colors inline-flex flex-col items-center justify-center mb-2 sm:mb-4 w-full sm:w-auto max-w-xl text-white shadow-[0_0_40px_rgba(22,199,132,0.55)] border border-white/10 leading-tight" data-id="element-263">
        <span className="text-base sm:text-2xl tracking-tight" data-id="element-264">
          🚀 SÌ, VOGLIO IL KIT COMPLETO
        </span>
        <span className="text-[11px] sm:text-sm font-semibold text-white/90 mt-1" data-id="element-265">
          Accesso immediato
        </span>
      </a>

      {/* Trust badges */}
      <p className="relative text-text-sec text-[11px] sm:text-sm font-medium flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap" data-id="element-266">
        <span data-id="element-267">⚡ Accesso Immediato</span>
        <span data-id="element-268">•</span>
        <span data-id="element-269">🛡️ Garanzia 7 Giorni</span>
        <span data-id="element-270">•</span>
        <span data-id="element-271">💳 Pagamento Unico</span>
      </p>
    </section>;
}